/* Launch-city registry — mirrors backend/seeds/cities.sql.
 *
 * Single source of truth on the frontend for:
 *   · the LocationScreen suggestion list
 *   · the serviceability check on reverse-geocoded GPS results
 *   · the default delivery radius shown on Home
 *
 * Keep this in sync with backend/seeds/cities.sql. When the backend
 * ships a `GET /cities` endpoint, swap the static list for an
 * `api.get('/cities')` call and treat this file as the mock fallback.
 *
 * `aliases` accommodate reverse-geocode variations (Mumbai/Bombay,
 * Ahmedabad/Amdavad, etc.) so users granting GPS permission don't get
 * a false "no suppliers here" warning.
 */

export const LAUNCH_CITIES = [
  { name: 'Mumbai',      state: 'Maharashtra', tier: 1, radiusKm: 5,
    aliases: ['Bombay', 'Greater Mumbai', 'Mumbai Suburban'],
    popularAreas: ['Bandra West', 'Andheri West', 'Powai', 'Lower Parel', 'Juhu', 'Goregaon'] },
  { name: 'Thane',       state: 'Maharashtra', tier: 1, radiusKm: 5,
    aliases: [],
    popularAreas: ['Hiranandani Estate', 'Ghodbunder Road', 'Majiwada', 'Naupada', 'Wagle Estate'] },
  { name: 'Pune',        state: 'Maharashtra', tier: 1, radiusKm: 5,
    aliases: ['Poona'],
    popularAreas: ['Koregaon Park', 'Kalyani Nagar', 'Baner', 'Viman Nagar', 'Kothrud'] },
  { name: 'Ahmedabad',   state: 'Gujarat',     tier: 1, radiusKm: 5,
    aliases: ['Amdavad'],
    popularAreas: ['Satellite', 'Vastrapur', 'Bodakdev', 'SG Highway', 'Prahlad Nagar'] },
  { name: 'Surat',       state: 'Gujarat',     tier: 1, radiusKm: 5,
    aliases: [],
    popularAreas: ['Adajan', 'Vesu', 'Athwa', 'City Light', 'Piplod'] },
  { name: 'Nagpur',      state: 'Maharashtra', tier: 2, radiusKm: 8,
    aliases: [],
    popularAreas: ['Dharampeth', 'Civil Lines', 'Manish Nagar', 'Sadar'] },
  { name: 'Nashik',      state: 'Maharashtra', tier: 2, radiusKm: 8,
    aliases: ['Nasik'],
    popularAreas: ['Gangapur Road', 'College Road', 'Indira Nagar', 'Govind Nagar'] },
  { name: 'Vadodara',    state: 'Gujarat',     tier: 2, radiusKm: 8,
    aliases: ['Baroda'],
    popularAreas: ['Alkapuri', 'Akota', 'Karelibaug', 'Manjalpur'] },
  { name: 'Rajkot',      state: 'Gujarat',     tier: 2, radiusKm: 10,
    aliases: [],
    popularAreas: ['Race Course', 'University Road', 'Kalavad Road', 'Mavdi'] },
  { name: 'Gandhinagar', state: 'Gujarat',     tier: 3, radiusKm: 15,
    aliases: [],
    popularAreas: ['Sector 21', 'Sector 11', 'Infocity', 'Kudasan'] },
  { name: 'Jamnagar',    state: 'Gujarat',     tier: 3, radiusKm: 15,
    aliases: [],
    popularAreas: ['Patel Colony', 'Indira Marg', 'Khambhalia Naka', 'Summair Club Road'] },
];

/** Flat "Area, City" strings for the LocationScreen autocomplete. */
export const LOCATION_SUGGESTIONS = LAUNCH_CITIES.flatMap((c) =>
  c.popularAreas.map((a) => `${a}, ${c.name}`),
);

/** Default location used when nothing has been picked yet (e.g. dev tweak-mode). */
export const DEFAULT_LOCATION = 'Bandra West, Mumbai';

/**
 * Check whether a reverse-geocoded city name belongs to our launch set.
 * Accepts variations via the `aliases` list.
 */
export function isServiceableCity(cityName) {
  if (!cityName) return false;
  const lc = String(cityName).toLowerCase();
  return LAUNCH_CITIES.some((c) =>
    lc.includes(c.name.toLowerCase()) ||
    c.aliases.some((a) => lc.includes(a.toLowerCase())),
  );
}

/** Look up a city record from a free-form name (returns null if not in the launch set). */
export function findCity(cityName) {
  if (!cityName) return null;
  const lc = String(cityName).toLowerCase();
  return LAUNCH_CITIES.find((c) =>
    lc.includes(c.name.toLowerCase()) ||
    c.aliases.some((a) => lc.includes(a.toLowerCase())),
  ) || null;
}
